import type { TutorFieldSchema, TutorSchema } from '@libs/data-access/entities'
import { Controller } from '@libs/ui/form'
import { useCommonAppTranslation } from '@libs/utils/translations'
import { useMemoSelector } from '@spa/redux/hooks'
import type { FC, ReactNode } from 'react'
import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { getCurrentAccountId } from 'snap-redux/modules/accounts/selectors'
import {
  fetchAllMemberships,
  fetchMembershipsTutors
} from 'snap-redux/modules/memberships'
import { getIsMembershipsLoading } from 'snap-redux/modules/memberships/selectors'
import { useRoleRights } from 'snap-redux/modules/memberships/utils'
import { useActions } from 'snap-redux/utils'

const actionsCreator = { fetchAllMemberships, fetchMembershipsTutors }

type Props = {
  label?: ReactNode
  locationId: number
  teamId: number
  disabled?: boolean
  placeholder?: string
  defaultValue?: TutorSchema
}

export const TutorAsyncSelect: FC<Props> = ({
  label,
  locationId,
  defaultValue,
  teamId,
  disabled,
  placeholder
}) => {
  const { t } = useCommonAppTranslation()
  const { atLeast } = useRoleRights()
  const actions = useActions(actionsCreator)
  const accountId = useMemoSelector(getCurrentAccountId)
  // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
  const [searchParam, setSearchParam] = useState<string>(null)
  const [tutorOptions, setTutorOptions] = useState<
    { label: string; value: string }[]
  >(
    defaultValue
      ? [
          {
            value: defaultValue?.id.toString(),
            label: t('models.fullname', {
              value: {
                firstname: defaultValue.firstname,
                lastname: defaultValue.lastname
              }
            })
          }
        ]
      : []
  )

  const {
    control,
    formState: { errors }
  } = useFormContext<TutorFieldSchema>()

  useEffect(() => {
    if (searchParam) {
      actions
        .fetchMembershipsTutors(accountId, {
          search: searchParam,
          locationId: atLeast.owner ? null : locationId,
          teamIds: atLeast.owner ? null : [teamId]
        })
        .then(data => {
          setTutorOptions(
            data.map(member => {
              return {
                value: member.id.toString(),
                label: t('models.fullname', {
                  value: {
                    firstname: member.firstname,
                    lastname: member.lastname
                  }
                })
              }
            })
          )
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam])

  const isMembershipLoading = useMemoSelector(getIsMembershipsLoading)

  return (
    <Controller.AsyncSelect
      errorMessage={errors?.tutor_membership_id?.message}
      name='tutor_membership_id'
      clearable
      control={control}
      onDebouncedSearchChange={setSearchParam}
      debouncedSearchDelay={500}
      disabled={disabled}
      label={label}
      options={tutorOptions}
      loading={isMembershipLoading}
      placeholder={placeholder || t('selectTutor.placeholder')}
    />
  )
}
